import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Home as HomeIcon, Layers, PaintRoller, Landmark, Blocks, Hammer, Check } from "lucide-react";
import { useContent } from "../context/ContentContext";
import { useReveal } from "../hooks/useReveal";
import PageHero from "../components/PageHero";
import Seo from "../components/Seo";

const iconMap = { Home: HomeIcon, Layers, PaintRoller, Landmark, Blocks, Hammer };

const ServiceRow = ({ s, index }) => {
  const r = useReveal();
  const Icon = iconMap[s.icon] || HomeIcon;
  const reversed = index % 2 === 1;
  return (
    <div ref={r.ref} className={`grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-16 items-center ${r.className}`}>
      <div className={`overflow-hidden ${reversed ? "lg:order-2" : ""}`}>
        <img src={s.image} alt={s.title} className="w-full h-[360px] md:h-[440px] object-cover" />
      </div>
      <div className={reversed ? "lg:order-1" : ""}>
        <Icon size={32} strokeWidth={1.1} className="text-[#94664f]" />
        <h2 className="section-title text-[32px] md:text-[42px] mt-5 mb-5">{s.title}</h2>
        <p className="font-body text-[15.5px] leading-[2] text-[#5b564d] mb-6">{s.description}</p>
        <Link to="/contact" className="inline-flex items-center gap-2 text-[#23211e] font-body uppercase tracking-[0.16em] text-[12px] hover:text-[#94664f] transition-colors group">
          Enquire about this service <ArrowRight size={15} className="group-hover:translate-x-1 transition-transform" />
        </Link>
      </div>
    </div>
  );
};

const Services = () => {
  const { services, company } = useContent();
  return (
    <main className="pt-[76px]">
      <Seo
        title="Building Services"
        description="New build homes, extensions & conversions, refurbishment, heritage & listed building restoration, brickwork, stonework, carpentry and joinery — delivered across London, The Home Counties and The Cotswolds."
        path="/services"
      />
      <PageHero
        eyebrow="Our Services"
        title="Turnkey Construction Services"
        subtitle="From ground to roof, we offer a full range of building services delivered by our small, trusted team of experienced tradesmen across London, The Home Counties and The Cotswolds."
        image="/services-roof.webp"
      />

      <section className="max-w-[1240px] mx-auto px-5 md:px-8 py-24 md:py-32 space-y-24 md:space-y-32">
        {services.map((s, i) => (
          <ServiceRow key={s.slug} s={s} index={i} />
        ))}
      </section>

      <section className="bg-[#f6f2ec] py-24 md:py-28">
        <div className="max-w-[1240px] mx-auto px-5 md:px-8 grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          <div>
            <p className="eyebrow mb-5">Integrity &middot; Professionalism &middot; Quality</p>
            <h2 className="section-title text-[32px] md:text-[46px] mb-6">A single, accountable point of contact</h2>
            <p className="font-body text-[15.5px] leading-[2] text-[#5b564d]">
              Because the same small, trusted team looks after every stage, your project stays coordinated, on programme and to the exacting standard you expect. We work with integrity and professionalism from first estimate to final finish &mdash; honest communication, no hidden costs and quality that speaks for itself.
            </p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
            {["Fully insured & accredited", "Small, experienced team", "Fixed, transparent estimates", "Full members of the Association for Project Management", "Clean & considerate sites", "Free initial consultation"].map((t, i) => (
              <div key={i} className="flex items-center gap-3 bg-white p-4">
                {t.includes("Association for Project Management") ? (
                  <img src={company.apmBadge} alt="Association for Project Management - Full Member (MAPM)" className="h-11 w-auto shrink-0" />
                ) : (
                  <Check size={16} className="text-[#94664f] shrink-0" />
                )}
                <span className="font-body text-[14px] text-[#3a3833]">{t}</span>
              </div>
            ))}
          </div>
        </div>
      </section>
    </main>
  );
};

export default Services;
